import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, ShoppingBag } from 'lucide-react';
import { posRestaurant } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import { useTables } from './hooks/useTables';
import FloorView from './FloorView';
import OrderDetailPanel from './OrderDetailPanel';
import BillingModal from './BillingModal';

function PaxModal({ table, onConfirm, onClose }) {
  const [pax, setPax] = useState(1);
  const overCapacity = table.capacity && pax > table.capacity;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-6">
        <div className="text-center mb-5">
          {table.section && (
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{table.section}</p>
          )}
          <p className="text-2xl font-bold text-gray-900">{table.name}</p>
          {table.capacity && (
            <p className="text-xs text-gray-400 mt-0.5">Max {table.capacity} seats</p>
          )}
        </div>

        <p className="text-sm font-semibold text-gray-700 text-center mb-3">Number of Guests</p>

        <div className="flex items-center justify-center gap-5 mb-4">
          <button
            type="button"
            onClick={() => setPax(p => Math.max(1, p - 1))}
            className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-xl font-bold text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            −
          </button>
          <span className="w-12 text-center text-3xl font-bold text-gray-900">{pax}</span>
          <button
            type="button"
            onClick={() => setPax(p => Math.min(50, p + 1))}
            className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center text-xl font-bold text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            +
          </button>
        </div>

        {overCapacity && (
          <p className="text-xs text-amber-600 text-center mb-3">
            ⚠ Exceeds table capacity ({table.capacity})
          </p>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(pax)}
            className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FloorPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { posToken, setTable, setAddToOrder } = usePosStore();

  const [panelTable, setPanelTable] = useState(null);
  const [panelOrder, setPanelOrder] = useState(null);
  const [paxTable, setPaxTable] = useState(null);
  const [billingOrder, setBillingOrder] = useState(null);

  const { tables, isLoading: tablesLoading, error: tablesError, refetch: refetchTables } = useTables();

  const ordersQuery = useQuery({
    queryKey: ['pos', 'active-orders', posToken],
    queryFn: async () => {
      const res = await posRestaurant.activeOrders(posToken);
      const map = {};
      for (const o of (res.data.orders || [])) {
        if (o.tableId) map[o.tableId] = o;
      }
      return map;
    },
    enabled: !!posToken,
    refetchInterval: 15_000,
    staleTime: 5_000,
  });

  const activeOrdersMap = ordersQuery.data || {};

  const handleRefresh = () => {
    refetchTables();
    ordersQuery.refetch();
  };

  const handleSelectAvailable = (table) => {
    setPaxTable(table);
  };

  const handlePaxConfirm = (pax) => {
    setTable(paxTable._id, paxTable.name, pax);
    setPaxTable(null);
    navigate('/pos/orders');
  };

  const handleViewOrder = (table, order) => {
    setPanelTable(table);
    setPanelOrder(order);
  };

  const handleClosePanel = () => {
    setPanelTable(null);
    setPanelOrder(null);
  };

  const handleAddItems = () => {
    if (panelOrder) {
      setAddToOrder(
        panelOrder._id,
        panelOrder.tableId,
        panelOrder.tableName,
        panelOrder.pax || 1,
        panelOrder.waiterId,
        panelOrder.waiterName,
      );
    }
    handleClosePanel();
    navigate('/pos/orders');
  };

  const handleBillOut = (updatedOrder) => {
    setBillingOrder(updatedOrder);
    handleClosePanel();
    handleRefresh();
  };

  const handleOrderMoved = () => {
    handleClosePanel();
    handleRefresh();
  };

  const handleOrderSplit = () => {
    handleRefresh();
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between flex-shrink-0">
        <h2 className="font-semibold text-gray-900 text-sm">Floor View</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/pos/orders')}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <ShoppingBag className="h-4 w-4" /> New Order
          </button>
          <button
            onClick={handleRefresh}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-hidden flex">
        {/* Floor grid */}
        <div className="flex-1 overflow-hidden">
          <FloorView
            tables={tables}
            activeOrdersMap={activeOrdersMap}
            isLoading={tablesLoading}
            error={tablesError}
            onRefresh={handleRefresh}
            onSelectAvailable={handleSelectAvailable}
            onViewOrder={handleViewOrder}
          />
        </div>

        {/* Order detail slide-in */}
        {panelTable && panelOrder && (
          <div className="w-96 flex-shrink-0 border-l border-gray-200 overflow-hidden">
            <OrderDetailPanel
              table={panelTable}
              order={panelOrder}
              tables={tables}
              onClose={handleClosePanel}
              onAddItems={handleAddItems}
              onBillOut={handleBillOut}
              onOrderMoved={handleOrderMoved}
              onOrderSplit={handleOrderSplit}
            />
          </div>
        )}
      </div>

      {/* Pax step overlay */}
      {paxTable && (
        <PaxModal
          table={paxTable}
          onConfirm={handlePaxConfirm}
          onClose={() => setPaxTable(null)}
        />
      )}

      {billingOrder && (
        <BillingModal
          isOpen={!!billingOrder}
          order={billingOrder}
          onClose={() => { setBillingOrder(null); handleRefresh(); }}
          onPaid={() => { setBillingOrder(null); handleRefresh(); }}
        />
      )}
    </div>
  );
}
